/**
 * PrescreenAllPane — the all-visible prescreen for Call Mode. Every question on
 * one scrollable surface so the recruiter can fill answers OUT OF ORDER as the
 * patient volunteers them, with a live pass/fail chip per question. Shares the
 * exact attempt + optimistic-save plumbing with PrescreenRunner via
 * usePrescreenAttempt — no second copy of the save logic.
 *
 * Future-transcript seam: `onApiReady` hands the parent a `setAnswerByKey`
 * adapter. A later live-transcript / AI-autofill panel writes suggested answers
 * through this — i.e. the SAME optimistic save path as manual entry — so it drops
 * in without touching this component. The parent must memoize `onApiReady`.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { PrescreenCriterion, studyInternalLabel } from '../../types';
import { notifyStatsChanged, describeFailingAnswer } from '../../lib';
import { usePromptDialog } from '../ui';
import FormRenderer from '../forms/FormRenderer';
import DisqualifyModal from './DisqualifyModal';
import CalcReadout from './CalcReadout';
import { usePrescreenAttempt } from './usePrescreenAttempt';
import RandomizedConflictModal from '../RandomizedConflictModal';

export interface PrescreenPaneApi {
  setAnswerByKey: (questionKey: string, value: any) => void;
}

interface Props {
  contactId: string;
  study: { id: string; title: string };
  onFinished: () => void;
  onClose?: () => void;
  onPivot?: (study: { id: string; title: string }) => void;
  /** Future-transcript seam — receives a stable answer-setter (or null on unmount). */
  onApiReady?: (apiObj: PrescreenPaneApi | null) => void;
}

export default function PrescreenAllPane({ contactId, study, onFinished, onClose, onPivot, onApiReady }: Props) {
  const ps = usePrescreenAttempt(contactId, study.id);
  const prompt = usePromptDialog();
  const [dqFor, setDqFor] = useState<PrescreenCriterion | null | 'summary'>(null);
  const [finishing, setFinishing] = useState(false);

  // Flush queued / in-flight answer saves if this pane unmounts mid-call (e.g.
  // the workspace closes) so a retry-queued answer is never silently lost.
  const flushRef = useRef(ps.flushPending);
  flushRef.current = ps.flushPending;
  useEffect(() => () => { flushRef.current(); }, []);

  const criteria = ps.criteria;
  const failing = criteria.filter((c) => ps.verdictFor(c) === 'fail');
  const unknownCount = criteria.filter((c) => ps.verdictFor(c) === 'unknown').length;
  const passCount = criteria.length - failing.length - unknownCount;
  // The actual failing answer(s) — pre-fills the DQ "Details" so the recorded
  // reason captures the disqualifying VALUE, not just the question.
  const failingDetails = failing.map((c) => describeFailingAnswer(c, ps.resolvedAnswers[c.question_key])).join('\n');

  // Seam: expose a key->setAnswer adapter to the parent (for future autofill).
  useEffect(() => {
    if (!onApiReady) return;
    const byKey = new Map(criteria.map((c) => [c.question_key, c]));
    onApiReady({
      setAnswerByKey: (key, value) => {
        const c = byKey.get(key);
        if (c) ps.setAnswer(c, value);
      },
    });
    return () => onApiReady(null);
  }, [onApiReady, criteria, ps.setAnswer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark eligible. With failing criteria present, `override` is required — the
  // recruiter confirms (and may note why); the override is recorded server-side.
  const markEligible = async (override = false) => {
    if (!ps.attempt) return;
    let overrideReason: string | null = null;
    if (override) {
      const r = await prompt({
        title: `Mark this patient ELIGIBLE despite ${failing.length} criteria that did not meet?`,
        message: 'This overrides the prescreen result and is recorded on the timeline.',
        label: 'Optionally note why',
        initialValue: '',
        confirmLabel: 'Mark eligible',
        danger: true,
      });
      if (r === null) return; // cancelled
      overrideReason = r.trim() || null;
    }
    setFinishing(true);
    try {
      await ps.flushPending();
      await api.patch(`/api/crm/attempts/${ps.attempt.id}`, {
        status: 'eligible',
        ...(override ? { override: true, override_reason: overrideReason, override_criteria: failing.map((c) => c.display_question) } : {}),
      });
      notifyStatsChanged();
      onFinished();
      onClose?.();
    } catch (err: any) {
      ps.setErrorMsg(err?.response?.data?.error || 'Could not mark eligible');
      setFinishing(false);
    }
  };

  if (ps.phase === 'starting') {
    return (
      <div className="flex items-center gap-3 p-4 text-sm text-gray-600 dark:text-gray-300">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600" /> Starting prescreen for {studyInternalLabel(study)}…
      </div>
    );
  }
  if (ps.phase === 'no_prescreen' || ps.phase === 'error') {
    return (
      <div className="p-4 text-sm text-gray-700 dark:text-gray-300">
        {/* Randomized elsewhere (plan §5.2 site 4): the guard blocked the attempt
            create. Acknowledging re-runs the start mid-call. */}
        {ps.conflict && (
          <RandomizedConflictModal
            targetStudyTitle={studyInternalLabel(study)}
            randomizedStudyTitle={ps.conflict.randomizedStudyTitle}
            randomizedAt={ps.conflict.randomizedAt}
            onCancel={ps.dismissConflict}
            onConfirm={ps.acknowledgeConflict}
          />
        )}
        {ps.phase === 'no_prescreen'
          ? `${studyInternalLabel(study)} has no published prescreen yet — an admin can create one in Studies.`
          : ps.errorMsg}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Scrollable question list */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-1">
        {criteria.map((c, i) => {
          const verdict = ps.verdictFor(c);
          return (
            <div
              key={c.id}
              className={`rounded-xl border p-2.5 ${
                verdict === 'fail'
                  ? 'border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10'
                  : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
              }`}
            >
              <div className="mb-1.5 flex items-start justify-between gap-2">
                <span className="text-xs font-medium text-gray-400">{i + 1}. {c.criterion_kind}</span>
                {verdict === 'fail' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    <AlertTriangle className="h-3 w-3" /> Does not meet
                  </span>
                )}
                {verdict === 'pass' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    <Check className="h-3 w-3" /> OK
                  </span>
                )}
              </div>
              {c.calculation ? (
                <CalcReadout label={c.display_question} value={ps.resolvedAnswers[c.question_key]} verdict={verdict} />
              ) : (
                <FormRenderer
                  mode="single"
                  dense
                  questions={[{
                    key: c.question_key,
                    label: c.display_question,
                    type: c.answer_type,
                    options: c.options,
                  }]}
                  answers={{ [c.question_key]: ps.answers[c.question_key] }}
                  onAnswer={(_, value) => ps.setAnswer(c, value)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Footer summary + actions */}
      <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
        <div className="mb-2 flex items-center justify-center gap-3 text-xs">
          <span className="text-green-600 dark:text-green-400">{passCount} pass</span>
          <span className="text-red-600 dark:text-red-400">{failing.length} fail</span>
          <span className="text-gray-500">{unknownCount} judgment</span>
        </div>
        {ps.errorMsg && <p className="mb-2 text-center text-sm text-red-600 dark:text-red-400">{ps.errorMsg}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => { ps.flushPending(); setDqFor(failing[0] || 'summary'); }}
            disabled={finishing}
            className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 disabled:opacity-50"
          >
            Disqualify…
          </button>
          {failing.length > 0 ? (
            <button
              type="button"
              onClick={() => markEligible(true)}
              disabled={finishing}
              title="Override the screener and mark this patient eligible anyway (recorded)"
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
            >
              <Check className="h-4 w-4" /> {finishing ? 'Saving…' : 'Override: mark eligible'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => markEligible(false)}
              disabled={finishing}
              className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-green-700 disabled:opacity-50"
            >
              <Check className="h-4 w-4" /> {finishing ? 'Saving…' : 'Mark eligible'}
            </button>
          )}
        </div>
      </div>

      {dqFor && ps.attempt && (
        <DisqualifyModal
          contactId={contactId}
          study={study}
          attemptId={ps.attempt.id}
          criteria={criteria}
          preselectedCriterion={dqFor === 'summary' ? null : dqFor}
          prefillDetails={failingDetails}
          onClose={() => setDqFor(null)}
          onRecorded={() => {
            setDqFor(null);
            notifyStatsChanged();
            onFinished();
            onClose?.();
          }}
          onPivot={onPivot}
        />
      )}
    </div>
  );
}
